import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { changeAppMicAccess, changePageDetails, changeSelectedMic } from '../../redux/appConfigSlice';
import { Card } from '../../components/Card';
import { Dropdown } from '../../components/Dropdown';
import { DropdownItem } from '../../components/Dropdown/DropdownItem';
import { Loader } from '../../components/Loader/Loader';
import { MicrophoneErrorCard } from '../../components/MicrophoneErrorCard/MicrophoneErrorCard';
import { MicObject } from '../../interface/interface';
import classes from './HeadsetSelectionScreen.module.css';

const HeadsetSelectionScreen = () => {
    const dispatch = useDispatch();

    const { pageObject, appMicAccess, selectedMic } = useSelector((state: any) => state.appConfigSlice);

    const [micList, setMicList] = useState<MicObject[] | null>(null);

    const checkMicrophoneAccess = async () => {
        try {
            const stream = await navigator.mediaDevices.getUserMedia({
                audio: true,
            });

            dispatch(changeAppMicAccess(true));

            //stop immediately
            stream.getTracks().forEach((track) => track.stop());
        } catch (error) {
            dispatch(changeAppMicAccess(false));
        }
    };

    const loadMicrophones = async () => {
        try {
            const devices = await navigator.mediaDevices.enumerateDevices();

            const microphones = devices
                .filter((device) => device.kind === 'audioinput')
                .filter((mic) => /^[0-9a-f]+$/i.test(mic.deviceId))
                .map(
                    (mic): MicObject => ({
                        label: mic.label,
                        deviceId: mic.deviceId,
                    }),
                );

            console.log('Microphones:', microphones);

            setMicList(microphones);

            // no mics
            if (microphones.length === 0) {
                dispatch(
                    changeSelectedMic({
                        deviceId: '',
                        label: '',
                    }),
                );

                dispatch(
                    changePageDetails({
                        ...pageObject,
                        micListAvailable: false,
                    }),
                );

                return;
            }

            // preserve existing selected if still connected
            const existingMic = microphones.find((mic) => mic.deviceId === selectedMic?.deviceId);
            const micToSelect = existingMic || microphones[0];

            dispatch(changeSelectedMic(micToSelect));
            dispatch(
                changePageDetails({
                    ...pageObject,
                    micListAvailable: true,
                }),
            );
        } catch (error) {
            console.error('Failed to enumerate microphones', error);
        }
    };

    const handleSelectMicrophone = (value: string) => {
        const selected = micList?.find((mic) => mic.deviceId === value);

        if (!selected) return;

        dispatch(changeSelectedMic(selected));
    };

    useEffect(() => {
        let intvl: ReturnType<typeof setInterval> | undefined;

        checkMicrophoneAccess()

        if (appMicAccess) {
            loadMicrophones();
        } else {
            intvl = setInterval(checkMicrophoneAccess, 1000);
        }

        navigator.mediaDevices.addEventListener('devicechange', loadMicrophones);

        return () => {
            navigator.mediaDevices.removeEventListener('devicechange', loadMicrophones);
            if (intvl) clearInterval(intvl);
        };
    }, [appMicAccess]);

    const selectedMicId = selectedMic?.deviceId || '';
    const showLoader = !appMicAccess;
    const showMicError = appMicAccess && micList && !micList?.length;
    const showMicDropdown = appMicAccess && !!micList?.length;
  
    return (
        <div className={classes.container} data-testid="microphone-check-container">
            {showLoader && (
                <Card className={classes.loader}>
                    <Loader id="test" micAccess />
                </Card>
            )}

            {showMicDropdown && (
                <Card className={classes.micSelection}>
                    <div className={classes.micDiv}>
                        <Dropdown
                            id="microphone"
                            value={selectedMicId}
                            onSelect={handleSelectMicrophone}
                            label="Choose your headset"
                        >
                            {micList.map((mic) => (
                                <DropdownItem key={mic.deviceId} label={mic.label} value={mic.deviceId} />
                            ))}
                        </Dropdown>
                    </div>
                </Card>
            )}

            {showMicError && <MicrophoneErrorCard noMicAccess={false} />}
        </div>
    );
};

export default HeadsetSelectionScreen;
